/**
 * FeedbackOptionsSelector Component
 *
 * Checkbox UI for selecting which feedback types to enable on a critique session.
 * Used in session creation flow.
 *
 * Feature: 649-critique-feedback-options
 * Extended by: 651-n-comments-task
 */

import { useCallback, useEffect } from 'react';
import { Checkbox } from '@/components/atoms/Checkbox';
import { NCommentsSelector } from '@/components/composed/NCommentsSelector';
import type { FeedbackType, NCommentsConfig } from '@/types/photo-rating';
import { calculateDefaultNComments } from '@/types/photo-rating';

export interface FeedbackOptionsSelectorProps {
  /** Currently enabled feedback types */
  value: FeedbackType[];
  /** Callback when selection changes */
  onChange: (types: FeedbackType[]) => void;
  /** N Comments configuration (when n_comments is enabled) */
  nCommentsConfig?: NCommentsConfig;
  /** Callback when N Comments config changes */
  onNCommentsConfigChange?: (config: NCommentsConfig) => void;
  /** Number of photos in the collection (required for n_comments option) */
  photoCount?: number;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
}

const FEEDBACK_OPTIONS: Array<{
  type: FeedbackType;
  label: string;
  description: string;
  requiresPhotoCount?: boolean;
}> = [
  {
    type: 'general',
    label: 'Text Comments',
    description: 'Ask critics to view and optionally comment on each photo',
  },
  {
    type: 'rating',
    label: 'Star Ratings',
    description: 'Allow critics to rate photos 1-5 stars',
  },
  {
    type: 'n_comments',
    label: 'Request N Comments',
    description: 'Ask critics to comment on a specific number of photos',
    requiresPhotoCount: true,
  },
];

/**
 * Selector for feedback options on critique sessions
 *
 * At least one option must be selected.
 * Feature 651: Includes N Comments selector when enabled.
 */
export function FeedbackOptionsSelector({
  value,
  onChange,
  nCommentsConfig,
  onNCommentsConfigChange,
  photoCount,
  disabled = false,
  error,
}: FeedbackOptionsSelectorProps) {
  const hasNComments = value.includes('n_comments');

  // Initialize nCommentsConfig when n_comments is enabled
  useEffect(() => {
    if (hasNComments && !nCommentsConfig && photoCount && photoCount > 0 && onNCommentsConfigChange) {
      onNCommentsConfigChange({ targetCount: calculateDefaultNComments(photoCount) });
    }
  }, [hasNComments, nCommentsConfig, photoCount, onNCommentsConfigChange]);

  const handleToggle = useCallback(
    (type: FeedbackType) => {
      if (value.includes(type)) {
        // Don't allow removing the last option
        if (value.length === 1) {
          return;
        }
        onChange(value.filter((t) => t !== type));
      } else {
        onChange([...value, type]);
      }
    },
    [value, onChange]
  );

  const handleNCommentsChange = useCallback(
    (targetCount: number) => {
      if (onNCommentsConfigChange) {
        onNCommentsConfigChange({ targetCount });
      }
    },
    [onNCommentsConfigChange]
  );

  // Filter options based on photoCount availability
  const availableOptions = FEEDBACK_OPTIONS.filter(
    (option) => !option.requiresPhotoCount || (photoCount && photoCount > 0)
  );

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-foreground">Feedback Options</div>
      <p className="text-xs text-muted-foreground">
        Choose which types of feedback critics can provide. At least one must be selected.
      </p>
      <div className="space-y-2">
        {availableOptions.map((option) => {
          const isChecked = value.includes(option.type);
          const isDisabled = disabled || (isChecked && value.length === 1);
          const showNCommentsSelector = option.type === 'n_comments' && isChecked && photoCount && photoCount > 0;

          return (
            <div key={option.type}>
              <label
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  isChecked
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                } ${isDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={() => handleToggle(option.type)}
                  disabled={isDisabled}
                  aria-describedby={`${option.type}-description`}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{option.label}</div>
                  <div id={`${option.type}-description`} className="text-xs text-muted-foreground">
                    {option.description}
                  </div>
                </div>
              </label>
              {showNCommentsSelector && (
                <NCommentsSelector
                  value={nCommentsConfig?.targetCount || calculateDefaultNComments(photoCount)}
                  onChange={handleNCommentsChange}
                  photoCount={photoCount}
                  disabled={disabled}
                />
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

FeedbackOptionsSelector.displayName = 'FeedbackOptionsSelector';
